import { Col, Form, Input, InputNumber, Modal, Row, Select, TreeSelect } from 'antd';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import connectionMonitorApi from '../../../../../../api/ConnectionMonitorApi';
import folderApi from '../../../../../../api/FolderApi';
import projectApi from '../../../../../../api/ProjectApi';
import { FolderDataNode } from '../../../../../../model/elements';
import { Connection, ConnectionMonitor, Project } from '../../../../../../model/entities';
import notificationService from '../../../../../../services/NotificationService';
import stringService from '../../../../../../services/StringService';
import treeFolderService from '../../../../../../services/TreeFolderService';
import styles from './ConnectionMonitoringModal.module.scss';

/**
 * Returns the connection monitoring modal.
 * @props the props
 * @returns the connection monitoring modal
 */
const ConnectionMonitoringModal = (props: Props): React.ReactElement => {
    const { visible, connection, onCancel, onSave, connectionMonitorId } = props;
    /*** HOOKS ***/

    const [form] = Form.useForm();
    const intl = useIntl();
    const [connectionMonitor, setConnectionMonitor] = useState<ConnectionMonitor>();
    const [loading, setLoading] = useState<'loading' | 'saving' | 'loadingFolders'>();
    const [projects, setProjects] = useState<Project[]>([]);
    const [sourceFolderNodes, setSourceFolderNodes] = useState<FolderDataNode[]>([]);
    const [sourceFolderNode, setSourceFolderNode] = useState<FolderDataNode>();

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading('loading');

                const projects = await projectApi.list(connection.source!.provider, connection.source!.id);
                setProjects(projects);
                if (connectionMonitorId) {
                    // get connection monitor
                    const connectionMonitor = await connectionMonitorApi.get(connectionMonitorId);
                    setConnectionMonitor(connectionMonitor);

                    // get source folder node (currently selected)
                    let sourceFolderNode = sourceFolderNodes.find((f) => f.folder.id === connectionMonitor.folder?.id);
                    if (!sourceFolderNode) {
                        try {
                            const sourceFolder = await folderApi.get(
                                connectionMonitor.folder!.id!,
                                connection.source!.provider,
                                connection.source!.id!,
                                connectionMonitor.project!.id!,
                            );
                            sourceFolderNode = treeFolderService.createFolderNode(sourceFolder);
                        } catch (error) {
                            // folder has been deleted
                        }
                    }

                    setSourceFolderNode(sourceFolderNode);

                    // load form values
                    form.setFieldsValue(connectionMonitor);
                    form.setFieldValue(['folder'], sourceFolderNode?.folder.name);
                } else {
                    const connectionMonitor: ConnectionMonitor = { connectionId: connection.id };
                    setConnectionMonitor(connectionMonitor);

                    // load form values
                    form.setFieldsValue(connectionMonitor);
                }
            } catch (error) {
                notificationService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [connection.id, connection.source, connectionMonitorId, form, intl, sourceFolderNodes]);

    /*** METHODS ***/

    const cancel = () => {
        form.resetFields();
        onCancel();
    };

    const submit = async () => {
        try {
            const values = await form.validateFields();
            await save(values);
        } catch (error) {}
    };

    const save = async (values: any) => {
        try {
            setLoading('saving');

            const project = projects.find((p) => p.id === values.project.id);
            const selectedFolderNode =
                (treeFolderService.find(values.folder, sourceFolderNodes) as FolderDataNode) || sourceFolderNode;

            const connectionMonitorUpdated: ConnectionMonitor = Object.assign({}, connectionMonitor, {
                name: values.name,
                delay: values.delay,
                coolingOffPeriod: values.coolingOffPeriod,
                hubId: !project || !selectedFolderNode ? undefined : connection.source?.id,
                provider: !project || !selectedFolderNode ? undefined : connection.source?.provider,
                project: !project || !selectedFolderNode ? undefined : project,
                folder:
                    !project || !selectedFolderNode
                        ? undefined
                        : {
                              id: selectedFolderNode?.folder.id,
                              name:
                                  connectionMonitor?.folder?.id === selectedFolderNode?.folder.id
                                      ? connectionMonitor?.folder?.name
                                      : selectedFolderNode?.path,
                          },
            });
            connectionMonitorUpdated.id
                ? await connectionMonitorApi.update(connectionMonitorUpdated, connection)
                : await connectionMonitorApi.create(connectionMonitorUpdated, connection);
            form.resetFields();
            onSave();
        } catch (error: any) {
            if (error.response.status === 409 && error.response.data) {
                notificationService.displayError(error, intl, [
                    { status: 409, message: 'connectionMonitor.status.duplicate' },
                ]);
            } else {
                notificationService.displayError(error, intl);
            }
        } finally {
            setLoading(undefined);
        }
    };

    const listFolders = async (projectId?: string) => {
        try {
            setLoading('loadingFolders');

            // load source folders
            const sourceFolders = projectId
                ? await folderApi.list(connection.source!.provider, connection.source!.id!, projectId)
                : [];
            const sourceFolderNodes = sourceFolders.map((f) => treeFolderService.createFolderNode(f));
            setSourceFolderNodes(sourceFolderNodes);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    const loadSubfolderNodes = async (folderId: string) => {
        const sourceFolderNodesAux = [...sourceFolderNodes];
        await treeFolderService.loadSubnodes(folderId, sourceFolderNodesAux);
        setSourceFolderNodes(sourceFolderNodesAux);
    };

    /*** COMPONENTS ***/

    const projectOptions = projects
        .sort((a, b) => stringService.sort(a.name, b.name))
        .map((project) => (
            <Select.Option key={project.id} value={project.id}>
                {project.name}
            </Select.Option>
        ));

    return (
        <Modal
            title={
                connectionMonitorId ? (
                    <FormattedMessage id="connectionMonitor.edit" />
                ) : (
                    <FormattedMessage id="connectionMonitor.create" />
                )
            }
            open={visible}
            onCancel={cancel}
            onOk={submit}
            okButtonProps={{ disabled: loading === 'loading', loading: loading === 'saving' }}
            cancelButtonProps={{ disabled: loading === 'saving' }}
            width={1000}
        >
            <Form form={form} colon={false} layout="vertical">
                <Row gutter={[28, 0]}>
                    <Col span={12}>
                        <Form.Item
                            label={<FormattedMessage id="connectionMonitor.name" />}
                            name="name"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Input maxLength={200} size="large" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            label={<FormattedMessage id="connectionMonitor.delay" />}
                            name="delay"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <InputNumber min={0} size="large" className={styles.inputNumber} />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item
                            label={<FormattedMessage id="connectionMonitor.coolingOffPeriod" />}
                            name="coolingOffPeriod"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <InputNumber min={0} size="large" className={styles.inputNumber} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col span={12}>
                        <Form.Item label={<FormattedMessage id="connectionMonitor.project" />} name={['project', 'id']}>
                            <Select
                                size="large"
                                showSearch
                                filterOption={stringService.filterOptions}
                                onChange={(projectId: string) => listFolders(projectId)}
                                loading={loading === 'loading'}
                                disabled={!!connectionMonitorId}
                            >
                                {projectOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label={<FormattedMessage id="connectionMonitor.folder" />} name={['folder']}>
                            <TreeSelect
                                style={{ width: '100%' }}
                                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                                treeData={sourceFolderNodes}
                                loadData={(folderNode: any) => loadSubfolderNodes(folderNode.folder.id)}
                                allowClear
                                size="large"
                                loading={loading === 'loading' || loading === 'loadingFolders'}
                                disabled={!!connectionMonitorId}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default ConnectionMonitoringModal;

interface Props {
    visible?: boolean;
    connection: Connection;
    connectionMonitorId?: number;
    onSave: () => Promise<void>;
    onCancel: () => void;
}
